import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { logActivity } from "../utils/ActivityLog";
import { fetchProviderDetails } from "../utils/APIHelper";
import { formatPatientCount, formatTime } from "../utils/service";

// Generate Appointment ID outside component
function generateAppointmentId() {
  const randomSixDigits = Math.floor(100000 + Math.random() * 900000);
  return `APT-${randomSixDigits}`;
}

// Calculate "Since" year based on experience years
function calculateSinceYear(experienceYears: number): string {
  if (!experienceYears || experienceYears <= 0) return "N/A";
  const currentYear = new Date().getFullYear();
  const sinceYear = currentYear - experienceYears;
  return sinceYear.toString();
}

const INSURANCE_OPTIONS = [
  "Aetna",
  "Anthem Blue Cross",
  "Blue Shield of California",
  "Central Health Plan",
  "Cigna",
  "Health Net",
  "UnitedHealthcare",
  "Medical Medicaid",
  "VA veterans administration",
] as const;

export default function DoctorsProfileLayout() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem("token");

  // doctor state (from location.state OR will be fetched)
  const [doctor, setDoctor] = React.useState<any>(location.state?.doctor || null);
  const [loading, setLoading] = React.useState(!location.state?.doctor);

  // tabs + slot states
  const [tabIndex, setTabIndex] = React.useState(0);
  const [showAllSlots, setShowAllSlots] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Dayjs>(dayjs().add(1, "day"));
  const [selectedSlot, setSelectedSlot] = React.useState<string | null>(null);
  const [appointmentId] = React.useState(generateAppointmentId());

  // dialogs
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);

  // form fields
  const [visitType, setVisitType] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [insurance, setInsurance] = React.useState<string[]>([]);
  const [bookedSlots, setBookedSlots] = React.useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [isBooking, setIsBooking] = React.useState(false);

  // fallback fetch if doctor not in state
  React.useEffect(() => {
    const loadDoctorDetail = async () => {
      if (id) {
        const data = await fetchProviderDetails(token as any, id);
        setDoctor(data?.data || {});
        setLoading(false);
      }
    }

    loadDoctorDetail();
  }, [id, token]);

  // Fetch booked slots when date or doctor changes
  React.useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!doctor?.provider_id || !selectedDate) return;

      setLoadingSlots(true);
      try {
        const response = await fetch(
          `https://vivotal-backend.vercel.app/api/v1/appointments/booked-slots?provider_id=${doctor.provider_id}&appointment_date=${selectedDate.format("YYYY-MM-DD")}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        if (data.success) {
          // Extract time from booked slots - handle different API response formats
          const bookedTimes = data.data.booked_slots.map((slot: any) => {
            // Try different possible field names
            if (slot.time_slot_24hr) {
              return slot.time_slot_24hr;
            }
            if (slot.full_datetime) {
              // Extract HH:MM from "09:00:00" or "2025-12-25 09:00:00"
              const timeMatch = slot.full_datetime.match(/(\d{2}):(\d{2})/);
              return timeMatch ? `${timeMatch[1]}:${timeMatch[2]}` : null;
            }
            if (slot.appointment_time) {
              // Extract HH:MM from "09:00:00"
              return slot.appointment_time.substring(0, 5);
            }
            return null;
          }).filter(Boolean);
          setBookedSlots(bookedTimes || []);
        }
      } catch (error) {
        console.error("Error fetching booked slots:", error);
        setBookedSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchBookedSlots();
  }, [doctor?.provider_id, selectedDate, token]);

  if (loading) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>Loading doctor details...</Typography>
      </Box>
    );
  }

  if (!doctor) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="error">No doctor data found</Typography>
      </Box>
    );
  }

  // generate time slots
  function generateTimeSlots(start: string, end: string, interval: number): string[] {
    const slots: string[] = [];
    
    // Handle time format with or without seconds (09:00:00 or 09:00)
    const parseTime = (timeStr: string) => {
      const parts = timeStr.split(":").map(Number);
      return { hours: parts[0], minutes: parts[1] || 0 };
    };
    
    const startTime = parseTime(start);
    const endTime = parseTime(end);

    let current = new Date();
    current.setHours(startTime.hours, startTime.minutes, 0, 0);

    const endDateTime = new Date();
    endDateTime.setHours(endTime.hours, endTime.minutes, 0, 0);

    while (current < endDateTime) {
      const next = new Date(current.getTime() + interval * 60000);
      
      if (next > endDateTime) break;
      
      const formatSlotTime = (date: Date) => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      };

      slots.push(`${formatSlotTime(current)} - ${formatSlotTime(next)}`);
      current = next;
    }

    return slots;
  }

  // Use doctor's start_time and end_time to generate slots
  const doctorStartTime = doctor?.start_time || "09:00:00";
  const doctorEndTime = doctor?.end_time || "17:00:00";
  const timeSlots = generateTimeSlots(doctorStartTime, doctorEndTime, 30);
  
  // Check if a slot is booked by comparing start time
  const isSlotBooked = (slot: string) => {
    const slotStartTime = slot.split(" - ")[0]; // Extract "09:00" from "09:00 - 09:30"
    return bookedSlots.includes(slotStartTime);
  };
  
  // determine which slots are visible (limit to 6 unless "Show More" toggled)
  const visibleSlots = showAllSlots ? timeSlots : timeSlots.slice(0, 6);

  const handleNextClick = () => {
    if (!selectedSlot) {
      alert("Please select a time slot.");
      return;
    }
    setDialogOpen(true);
  };

  const handleBookSlot = async () => {
    if (!visitType || !reason || !insurance) {
      alert("Please fill all fields.");
      return;
    }

    setIsBooking(true);

    // Get patient ID from user context
    const patientId = user?.id || localStorage.getItem("patient_id");
    if (!patientId) {
      alert("Patient information not found. Please log in again.");
      setIsBooking(false);
      return;
    }

    // Get provider ID - try multiple possible field names
    const providerId = doctor?.provider_id;
    if (!providerId) {
      alert("Provider information not found. Please reload the page and try again.");
      console.error("Doctor object:", doctor);
      return;
    }

    // Extract time from slot (e.g., "09:00 - 09:30" -> "09:00:00")
    const appointmentTime = selectedSlot?.split(" - ")[0] + ":00";

    // Map visit type to API format
    const visitTypeMap: { [key: string]: string } = {
      "In Person Visit": "IN_PERSON",
      "Online Consultation": "VIRTUAL",
    };

    const payload = {
      patient_id: patientId,
      provider_id:  providerId,
      appointment_date: selectedDate.format("YYYY-MM-DD"),
      appointment_time: appointmentTime,
      visit_type: visitTypeMap[visitType] || "IN_PERSON",
      reason_for_visit: reason,
      // insurance_provider: insurance,
      insurance_provider: insurance.join(", "),
      insurance_policy_number: "BC123456789",
      insurance_group_number: "GRP001",
    };

    console.log("Booking appointment with payload:", payload);

    try {
      const response = await fetch(
        "https://vivotal-backend.vercel.app/api/v1/appointments",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      console.log("Appointment response:", data);

      if (!response.ok) {
        throw new Error(data.data?.errorMessage || data.message || "Failed to book appointment.");
      }

      // Log the appointment booking activity
      const appointmentTimeDisplay = appointmentTime.substring(0, 5); // Extract HH:MM
      logActivity(
        user?.id ? String(user.id) : undefined,
        "appointment_booked",
        `Booked appointment with Dr. ${doctor.first_name} ${doctor.last_name} on ${selectedDate.format("DD MMM YYYY")} at ${appointmentTimeDisplay}`,
        {
          appointmentId: appointmentId,
          providerId: doctor.provider_id,
          date: selectedDate.format("YYYY-MM-DD"),
          time: appointmentTimeDisplay,
        }
      );

      setDialogOpen(false);
      setConfirmationDialogOpen(true);
    } catch (error: any) {
      console.error("Error booking appointment:", error);
      alert("Error booking slot: " + error.message);
    } finally {
      setIsBooking(false);
    }
  };

   const formatSpecialization = (specs: string[] = [], visibleCount = 2) => {
    if (!Array.isArray(specs) || specs.length === 0) return "N/A";

    const visible = specs.slice(0, visibleCount);
    const remaining = specs.length - visibleCount;

    return remaining > 0
      ? `${visible.join(", ")} +${remaining} more`
      : visible.join(", ");
  };

  return (
    <Box p={2} sx={{ fontFamily: "Quicksand, sans-serif" }}>
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate("/user-layout/patient-dashboard")}
          sx={{ cursor: "pointer" }}
        >
          Dashboard
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate("/user-layout/doctor-list")}
          sx={{ cursor: "pointer" }}
        >
          Doctors
        </Link>
        <Typography color="text.primary">
          {doctor.title} {doctor.first_name} {doctor.last_name}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
        {/* Left panel */}
        <Paper elevation={2} sx={{ width: { xs: "100%", md: "68%" }, p: 2, borderRadius: 2 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Avatar src={doctor.avatar} sx={{ width: 64, height: 64 }} />
            <Box flex={1}>
              <Typography variant="h6" fontWeight="bold">{doctor.title} {doctor.first_name} {doctor.last_name}</Typography>
              <Box mt={1} display="flex" gap={2} flexWrap="wrap">
                {[{ label: "Since", value: calculateSinceYear(doctor.experience_years) },
                { label: "Patient", value: formatPatientCount(doctor?.statistics?.existingPatients || 0) },
                { label: "Specialty", value:  formatSpecialization(doctor.specialization) },
                { label: "Time", value: `${formatTime(doctor.start_time)} to ${formatTime(doctor.end_time)}` },
                { label: "License", value: doctor.license_number },
                { label: "Primary Specialty", value: formatSpecialization(doctor.primary_specialty) ??  formatSpecialization(doctor.specialization) },
                { label: "Location", value: doctor.location },
                { label: "Languages", value: "English" }].map(({ label, value }, i) => (
                  <Box key={i}>
                    <Typography variant="caption">{label}</Typography>
                    <Typography variant="subtitle2">{value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Typography variant="subtitle2" color="orange">4.5/5</Typography>
          </Box>

          <Tabs value={tabIndex} onChange={(_, i) => setTabIndex(i)} sx={{ mt: 2 }}>
            <Tab label="Profile" />
            <Tab label="Honours & Awards" />
          </Tabs>

          <Divider sx={{ my: 2 }} />

          {tabIndex === 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                About Specialist
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {doctor.title} {doctor.first_name} {doctor.last_name} is a skilled interventional {doctor.specialty} with {doctor.experience_years} years of post-training
                experience. Passionate about patient care and clinical excellence. {doctor.bio}
              </Typography>

              <Typography variant="subtitle1" fontWeight={600} mt={2}>
                Professional Experience
              </Typography>
              <ul style={{ paddingLeft: 20 }}>
                <li>Coronary Angiographies - 800 </li>
                <li>Coronary Angioplasties - 200 </li>
                <li>Permanent pacemaker implantation - 40–50</li>
                <li>ICD implants - 5–10</li>
                <li>Balloon mitral valvotomy – 30 </li>
              </ul>

              <Typography variant="subtitle1" fontWeight={600} mt={2}>
                Professional Affiliations
              </Typography>
              <ul style={{ paddingLeft: 20 }}>
                <li>Life member of ASE</li>
                <li>Life member of CSI</li>
                <li>Life member of IAE</li>
                <li>Life member of ICC</li>
              </ul>
            </>
          )}

          {tabIndex === 1 && (
            <Typography variant="body2">Honours & Awards content goes here.</Typography>
          )}
        </Paper>

        {/* Right panel */}
        <Paper elevation={1} sx={{ width: { xs: "100%", md: "30%" }, p: 2, borderRadius: 2, minWidth: 260 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Typography fontWeight={600} mb={1}>Select Appointment Date</Typography>
            <DatePicker
              value={selectedDate}
              onChange={(newValue) => {
                if (dayjs.isDayjs(newValue)) {
                  setSelectedDate(newValue);
                }
              }}
              minDate={dayjs().add(1, "day")}
              slotProps={{
                textField: { fullWidth: true, size: "small", sx: { mb: 2 } },
              }}
            />
          </LocalizationProvider>

          {selectedSlot && (
            <Typography
              variant="body2"
              fontWeight={600}
              color="primary"
              mb={1}
              textAlign="center"
            >
              Selected: {selectedSlot}
            </Typography>
          )}

          <Box display="flex" flexDirection="column" gap={1}>
            {loadingSlots ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              visibleSlots.map((slot, idx) => {
                const isBooked = isSlotBooked(slot);
                return (
                  <Button
                    key={idx}
                    variant={selectedSlot === slot ? "contained" : "outlined"}
                    size="small"
                    fullWidth
                    disabled={isBooked}
                    onClick={() => setSelectedSlot(slot)}
                    sx={{
                      opacity: isBooked ? 0.5 : 1,
                      textDecoration: isBooked ? "line-through" : "none",
                    }}
                  >
                    {slot} {isBooked && "(Booked)"}
                  </Button>
                );
              })
            )}
            {timeSlots.length > 6 && (
              <Button
                size="small"
                variant="text"
                onClick={() => setShowAllSlots((prev) => !prev)}
                sx={{ mt: 1 }}
              >
                {showAllSlots ? "Show Less" : "Show More"}
              </Button>
            )}
          </Box>

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2, backgroundColor: "#0ea5e9" }}
            onClick={handleNextClick}
          >
            Next
          </Button>
        </Paper>
      </Box>

      {/* Slot Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Confirm Slot</span>
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <FormControl fullWidth margin="dense" required>
            <InputLabel>Visit Type</InputLabel>
            <Select value={visitType} onChange={(e) => setVisitType(e.target.value)} label="Visit Type">
              <MenuItem value="In Person Visit">In Person Visit</MenuItem>
              <MenuItem value="Online Consultation">Online Consultation</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense" required>
            <InputLabel>Reason</InputLabel>
            <Select value={reason} onChange={(e) => setReason(e.target.value)} label="Reason">
              <MenuItem value="Follow Up">Follow Up</MenuItem>
              <MenuItem value="New Issue">New Issue</MenuItem>
              <MenuItem value="Consultation">Consultation</MenuItem>
              <MenuItem value="Routine Checkup">Routine Checkup</MenuItem>
            </Select>
          </FormControl>

          {/* <FormControl fullWidth margin="dense" required>
            <InputLabel>Insurance</InputLabel>
            <Select value={insurance} onChange={(e) => setInsurance(e.target.value as string[])} label="Insurance"
              renderValue={(selected)=> (selected as string[]).join(", ")} multiple
              >
              <MenuItem value="Aetna">Aetna</MenuItem>
              <MenuItem value="Anthem Blue Cross">Anthem Blue Cross</MenuItem>
              <MenuItem value="Blue Shield of California">Blue Shield of California</MenuItem>
              <MenuItem value="Central Health Plan">Central Health Plan</MenuItem>
              <MenuItem value="Cigna">Cigna</MenuItem>
              <MenuItem value="Health Net">Health Net</MenuItem>
              <MenuItem value="UnitedHealthcare">UnitedHealthcare</MenuItem>
              <MenuItem value="Medical Medicaid">Medical Medicaid</MenuItem>
              <MenuItem value="VA veterans administration">VA veterans administration</MenuItem>
            </Select>
          </FormControl> */}

          <FormControl fullWidth margin="dense" required>
            <InputLabel>Insurance</InputLabel>
            <Select
              multiple
              value={insurance}
              // onChange={(e) => setInsurance(e.target.value as string[])}
              onChange={(e) => {
                const selected = e.target.value as string[];
                if (selected.length <= 3) {
                  setInsurance(selected);
                } else {
                // alert("You can select maximum 3 insurance options");
                toast.warn('You can select maximum 3 insurance options', {autoClose: 5000});
                }
              }}
              label="Insurance"
              renderValue={(selected) => (selected as string[]).join(", ")}
            >
              {INSURANCE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={insurance.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={handleBookSlot}
            sx={{ backgroundColor: "#0ea5e9", mx: "auto" }}
            disabled={isBooking || !visitType || !reason || insurance.length === 0}
            startIcon={isBooking ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isBooking ? "Booking..." : "Book Slot"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmationDialogOpen} onClose={() => setConfirmationDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Appointment Confirmed</span>
          <IconButton onClick={() => setConfirmationDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar src={doctor.avatar} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">{doctor.name}</Typography>
              <Typography variant="body2" color="text.secondary">{doctor.specialty}</Typography>
            </Box>
          </Box>

          <Typography variant="body2" gutterBottom><strong>Appointment ID:</strong> {appointmentId}</Typography>
          <Typography variant="body2" gutterBottom><strong>Date:</strong> {selectedDate.format("DD MMM YYYY")}</Typography>
          <Typography variant="body2" gutterBottom><strong>Time:</strong> {selectedSlot}</Typography>
          <Typography variant="body2" gutterBottom><strong>Visit Type:</strong> {visitType}</Typography>
          <Typography variant="body2" gutterBottom><strong>Reason:</strong> {reason}</Typography>
          <Typography variant="body2" gutterBottom><strong>Insurance:</strong> {insurance}</Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmationDialogOpen(false);
              navigate("/user-layout/patient-appointment");
            }}
            sx={{ backgroundColor: "#0ea5e9", mx: "auto" }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
